import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { MouseEvent } from "react"
import { useDispatch } from "react-redux"
import { VisibilityOutlined } from "@mui/icons-material"
import { Alert, Box, Button, Card, Chip, CircularProgress, Link, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { log } from "helpers/logger"
import { useSkillCatalog } from "resources/skills"
import { useCreateScheduledWorkflow } from "resources/scheduled-workflows/hooks"
import type { CreateWorkflowDraft } from "resources/scheduled-workflows/draft-contract"
import type { ScheduledWorkflow } from "resources/scheduled-workflows/types"
import { computeNextRunAt, formatNextRun, formatRunTime, formatSchedule, formatScheduleDays, viewerTimeZone } from "../scheduled-workflows/schedule-format"
import { closeSkillsPane, setSurfaceView } from "../store/agent-chat-slice"
import { NO_NOTIFICATION, createDraftFailureOf } from "./create-draft-failure"
import type { CreateDraftFailure } from "./create-draft-failure"
import type { MarkdownArtifact } from "./artifact-contract"
import { toolDisplayLabel } from "./tool-labels"
import { useArtifactPanelActions } from "./use-artifact-panel"
import { ACTIONS_SX, CARD_SX, DESCRIPTION_SX, HEAD_SX, NAME_SX, PREVIEW_SX } from "./card-styles"

const SKILLS_SX = { display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" } satisfies SxProps<Theme>

const SHOWN_SKILLS = 3
const IN_FLIGHT_RETRY_MILLISECONDS = 2000
const MAX_IN_FLIGHT_ATTEMPTS = 10
const MANUAL_ONLY_SCHEDULE = "Runs only when you run it"

function doneMessage(workflow: ScheduledWorkflow, now: Date): string {
  if (workflow.archivedAt != null) return `"${workflow.name}" was created from this draft and later archived.`
  if (workflow.schedule == null) return `"${workflow.name}" is ready. Run it from Scheduled Workflows.`
  try {
    const { nextRunAt } = workflow
    if (nextRunAt != null) return `"${workflow.name}" is scheduled. First run ${formatRunTime(nextRunAt, now, viewerTimeZone())}.`
  } catch (error) {
    log(error)
    return `"${workflow.name}" is scheduled.`
  }
  return `"${workflow.name}" is scheduled.`
}

function scheduleLine(draft: CreateWorkflowDraft, now: Date): string {
  const { schedule } = draft.payload
  if (schedule == null) return MANUAL_ONLY_SCHEDULE
  try {
    const nextRunAt = computeNextRunAt(schedule, now)
    const viewerZone = viewerTimeZone()
    const line = formatSchedule(schedule, viewerZone, { now, nextRunAt })
    return nextRunAt === null ? line : `${line} · ${formatNextRun(nextRunAt, now, viewerZone)}`
  } catch (error) {
    log(error)
    return formatScheduleDays(schedule)
  }
}

function instructionsArtifact(draft: CreateWorkflowDraft, skillNames: string[]): MarkdownArtifact {
  const skills = skillNames.length === 0 ? [] : ["**Skills**", skillNames.map((skillName) => `- ${skillName}`).join("\n")]
  return {
    id: `workflow-draft-${draft.draftId}`,
    type: "markdown",
    title: draft.payload.name,
    markdown: [draft.payload.initialMessage, ...skills].join("\n\n"),
  }
}

export interface CreateDraftCardProps {
  draft: CreateWorkflowDraft
}

export function CreateDraftCard({ draft }: CreateDraftCardProps) {
  const { payload } = draft
  const { mutateAsync, isPending } = useCreateScheduledWorkflow(NO_NOTIFICATION)
  const { sections } = useSkillCatalog()
  const panel = useArtifactPanelActions()
  const dispatch = useDispatch()
  const [now] = useState(() => new Date())
  const scheduleLineText = useMemo(() => scheduleLine(draft, now), [draft, now])
  const [created, setCreated] = useState<{ workflow: ScheduledWorkflow, message: string } | null>(null)
  const [failure, setFailure] = useState<CreateDraftFailure | null>(null)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isUnmounted = useRef(false)
  const isRunning = useRef(false)

  useEffect(() => {
    isUnmounted.current = false
    return () => {
      isUnmounted.current = true
      isRunning.current = false
      if (retryTimer.current !== null) clearTimeout(retryTimer.current)
    }
  }, [])

  const catalogNames = useMemo(() => new Map(sections.flatMap((section) => section.skills.flatMap((skill) => [
    [skill.id, skill.name] as const,
    ...(skill.plugin == null ? [] : [[`${skill.plugin}/${skill.id}`, skill.name] as const]),
  ]))), [sections])
  const skillName = useCallback((skillId: string) => catalogNames.get(skillId) ?? skillId, [catalogNames])
  const skillNames = useMemo(() => payload.skills.map(skillName), [payload.skills, skillName])

  const onPreview = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    panel?.openArtifact(instructionsArtifact(draft, skillNames), event.currentTarget, payload.name)
  }, [panel, draft, skillNames, payload.name])

  const onCreate = useCallback(async () => {
    if (isRunning.current) return
    isRunning.current = true
    try {
      setFailure(null)
      let answer: CreateDraftFailure | null = { kind: "inFlight" }
      let attempts = 0
      while (answer?.kind === "inFlight" && !isUnmounted.current) {
        attempts += 1
        try {
          const workflow = await mutateAsync(draft)
          setCreated({ workflow, message: doneMessage(workflow, new Date()) })
          answer = null
        } catch (error) {
          const failureOfAttempt = createDraftFailureOf(error, payload.name, skillName)
          answer = failureOfAttempt.kind === "inFlight" && attempts >= MAX_IN_FLIGHT_ATTEMPTS ? { kind: "retryable" } : failureOfAttempt
          if (answer.kind === "retryable") log(error)
          setFailure(answer)
          if (answer.kind === "inFlight" && !isUnmounted.current) await new Promise<void>((resolve) => { retryTimer.current = setTimeout(resolve, IN_FLIGHT_RETRY_MILLISECONDS) })
        }
      }
    } finally {
      isRunning.current = false
    }
  }, [mutateAsync, draft, payload.name, skillName])

  const onConfirm = useCallback(() => { void onCreate() }, [onCreate])

  const openWorkflows = useCallback(() => {
    dispatch(closeSkillsPane())
    dispatch(setSurfaceView("workflows"))
  }, [dispatch])

  const isInFlight = failure?.kind === "inFlight"
  const isSaving = isPending || isInFlight
  const warningFailure = failure?.kind === "warning" ? failure : null
  const hiddenSkillCount = skillNames.length - SHOWN_SKILLS

  return (
    <Card variant="outlined" sx={CARD_SX} data-testid="kelly-workflow-draft">
      <Box sx={HEAD_SX}>
        <Typography sx={NAME_SX} data-testid="kelly-workflow-draft-name">{payload.name}</Typography>
        {panel !== null && (
          <Button
            size="small"
            variant="text"
            sx={PREVIEW_SX}
            startIcon={<VisibilityOutlined fontSize="small" />}
            onClick={onPreview}
            data-testid="kelly-workflow-draft-preview"
          >
            Preview instructions
          </Button>
        )}
      </Box>
      {payload.description != null && payload.description !== "" && (
        <Typography variant="body2" sx={DESCRIPTION_SX} title={payload.description}>{payload.description}</Typography>
      )}
      <Typography variant="body2" color="text.secondary">{scheduleLineText}</Typography>
      {skillNames.length > 0 && (
        <Box sx={SKILLS_SX}>
          {skillNames.slice(0, SHOWN_SKILLS).map((name, index) => <Chip key={`${payload.skills[index]}-${index}`} size="small" variant="outlined" label={name} />)}
          {hiddenSkillCount > 0 && <Chip size="small" variant="outlined" label={`+${hiddenSkillCount}`} />}
        </Box>
      )}
      {draft.unsupportedTools.length > 0 && (
        <Alert severity="warning" data-testid="kelly-workflow-draft-warning">
          {`This workflow can't use some tools Kelly used here (${draft.unsupportedTools.map((name) => toolDisplayLabel(undefined, name)).join(", ")}). Its runs skip those steps, so results can differ from this chat.`}
        </Alert>
      )}
      {created !== null ? (
        <Alert severity="success" data-testid="kelly-workflow-draft-done">
          {created.message}
          {created.workflow.archivedAt == null && created.workflow.schedule != null && (
            <> <Link component="button" variant="body2" onClick={openWorkflows}>Open in Scheduled Workflows</Link></>
          )}
        </Alert>
      ) : (
        <>
          {warningFailure !== null && (
            <Alert severity="warning" data-testid={warningFailure.testId}>{warningFailure.message}</Alert>
          )}
          {failure?.kind === "retryable" && (
            <Alert severity="error" data-testid="kelly-workflow-draft-error">
              Couldn&apos;t confirm it was saved. Try again.
            </Alert>
          )}
          {(warningFailure === null || warningFailure.keepsAction) && (
            <Box sx={ACTIONS_SX}>
              <Button
                variant="contained"
                size="small"
                disabled={isSaving}
                onClick={onConfirm}
                data-testid="kelly-workflow-draft-confirm"
                startIcon={isSaving ? <CircularProgress size={14} /> : undefined}
              >
                {isInFlight ? "Still creating…" : "Create workflow"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Card>
  )
}
